import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { ListRow } from "./useTopShopAdmin";

interface Crit { id: string; focus_area_id: string; name: string; min_points: number; max_points: number; sort_order: number; is_active: boolean }
const KEY = ["admin", "top-shop", "rubric-criteria"];

function CritRow({ row, onChanged, onMove, first, last }: { row: Crit; onChanged: () => void; onMove: (d: -1 | 1) => void; first: boolean; last: boolean }) {
  const [name, setName] = useState(row.name);
  const [min, setMin] = useState(String(row.min_points));
  const [max, setMax] = useState(String(row.max_points));
  useEffect(() => { setName(row.name); setMin(String(row.min_points)); setMax(String(row.max_points)); }, [row]);

  const update = async (patch: Partial<Crit>) => {
    const { error } = await supabase.from("program_challenge_criteria").update(patch).eq("id", row.id);
    if (error) toast.error(error.message); else onChanged();
  };
  const saveRange = () => {
    const a = Number(min), b = Number(max);
    if (a === row.min_points && b === row.max_points) return;
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0 || a >= b) {
      toast.error("Min and max must be whole numbers of 0 or more, with min less than max.");
      setMin(String(row.min_points)); setMax(String(row.max_points));
      return;
    }
    update({ min_points: a, max_points: b });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b py-2 last:border-0">
      <Input aria-label="Criterion name" className="flex-1 min-w-[160px]" value={name} onChange={(e) => setName(e.target.value)}
        onBlur={() => { const v = name.trim(); if (v && v !== row.name) update({ name: v }); else setName(row.name); }} />
      <Input aria-label="Min points" type="number" min={0} className="w-16" value={min} onChange={(e) => setMin(e.target.value)} onBlur={saveRange} />
      <span className="text-xs text-muted-foreground">to</span>
      <Input aria-label="Max points" type="number" min={1} className="w-16" value={max} onChange={(e) => setMax(e.target.value)} onBlur={saveRange} />
      <div className="flex items-center gap-1.5">
        <Switch checked={row.is_active} onCheckedChange={(v) => update({ is_active: v })} aria-label="Active" />
        <span className="text-xs text-muted-foreground w-12">{row.is_active ? "Active" : "Hidden"}</span>
      </div>
      <Button size="icon" variant="ghost" disabled={first} onClick={() => onMove(-1)} aria-label="Move up"><ArrowUp className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" disabled={last} onClick={() => onMove(1)} aria-label="Move down"><ArrowDown className="h-4 w-4" /></Button>
    </div>
  );
}

function AddCriterion({ areaId, nextOrder, onAdded }: { areaId: string; nextOrder: number; onAdded: () => void }) {
  const [name, setName] = useState("");
  const [min, setMin] = useState("0");
  const [max, setMax] = useState("10");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const a = Number(min), b = Number(max);
    if (!name.trim()) return;
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || a >= b) { toast.error("Min and max must be whole numbers of 0 or more, with min less than max."); return; }
    setBusy(true);
    const { error } = await supabase.from("program_challenge_criteria").insert({ focus_area_id: areaId, name: name.trim(), min_points: a, max_points: b, sort_order: nextOrder });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    setName(""); setMin("0"); setMax("10");
    toast.success("Criterion added");
    onAdded();
  };
  return (
    <div className="flex flex-wrap items-center gap-2 pt-2">
      <Input aria-label="New criterion name" placeholder="Add criterion" className="flex-1 min-w-[160px]" value={name} onChange={(e) => setName(e.target.value)} />
      <Input aria-label="New min points" type="number" className="w-16" value={min} onChange={(e) => setMin(e.target.value)} />
      <span className="text-xs text-muted-foreground">to</span>
      <Input aria-label="New max points" type="number" className="w-16" value={max} onChange={(e) => setMax(e.target.value)} />
      <Button size="sm" onClick={add} disabled={busy || !name.trim()}>
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Add criterion
      </Button>
    </div>
  );
}

export function TopShopRubricCard({ focusAreas }: { focusAreas: ListRow[] }) {
  const qc = useQueryClient();
  const areaIds = focusAreas.map((a) => a.id);
  const { data: criteria, isLoading } = useQuery({
    queryKey: [...KEY, areaIds.join(",")],
    enabled: areaIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from("program_challenge_criteria").select("*").in("focus_area_id", areaIds).order("sort_order");
      if (error) throw error;
      return (data ?? []) as Crit[];
    },
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: KEY });
    qc.invalidateQueries({ queryKey: ["admin", "top-shop"] });
    qc.invalidateQueries({ queryKey: ["top-shop"] });
  };
  const move = async (rows: Crit[], i: number, d: -1 | 1) => {
    const a = rows[i], b = rows[i + d];
    if (!a || !b) return;
    const bOrder = a.sort_order === b.sort_order ? b.sort_order + d : b.sort_order;
    const [r1, r2] = await Promise.all([
      supabase.from("program_challenge_criteria").update({ sort_order: bOrder }).eq("id", a.id),
      supabase.from("program_challenge_criteria").update({ sort_order: a.sort_order }).eq("id", b.id),
    ]);
    if (r1.error || r2.error) toast.error((r1.error ?? r2.error)!.message);
    refresh();
  };

  const sortedAreas = [...focusAreas].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="text-lg">Scoring rubric</CardTitle>
        <CardDescription>Changing a range does not change scores already entered. Deactivated criteria stop counting toward totals.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : sortedAreas.map((area) => {
          const rows = (criteria ?? []).filter((c) => c.focus_area_id === area.id);
          const activeMax = rows.filter((r) => r.is_active).reduce((s, r) => s + r.max_points, 0);
          return (
            <div key={area.id}>
              <div className="flex items-center justify-between border-b pb-1">
                <h4 className="font-semibold">{area.name}{!area.is_active && <span className="ml-2 text-xs font-normal text-muted-foreground">(area hidden)</span>}</h4>
                <span className="text-sm text-muted-foreground">Max {activeMax}</span>
              </div>
              {rows.map((r, i) => <CritRow key={r.id} row={r} onChanged={refresh} onMove={(d) => move(rows, i, d)} first={i === 0} last={i === rows.length - 1} />)}
              <AddCriterion areaId={area.id} nextOrder={rows.reduce((m, r) => Math.max(m, r.sort_order), 0) + 1} onAdded={refresh} />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
